"use client";
import { useSession } from "next-auth/react";
import Script from "next/script";
import { useEffect } from "react";
import { useActivityTracker } from "~/components/analytics/useActivityTracker";
import { useTrackingId } from "~/components/analytics/useTrackingId";
import {
  AnalyticsEvents,
  identifyUser,
  trackEvent,
} from "~/connectors/analytics.connector";
import { ANALYTICS_CONFIG } from "~/lib/constants";


interface UmamiEvent {
  type: string;
  payload: {
    name?: string;
    data?: Record<string, unknown>;
    id?: string;
    url?: string;
  };
}

const queuedEvents: UmamiEvent[] = [];

// Track if app-load event has been fired in this page load
// This persists across component remounts but resets on hard refresh
let isAppLoadTracked = false;

setInterval(() => {
  if (queuedEvents.length === 0) return;
  while (queuedEvents.length > 0) {
    const event = queuedEvents.shift();
    if (event) {
      const dataPayload = event.payload.data;
      const eventName = event.payload.name;
      if (eventName) {
        trackEvent(eventName, dataPayload);
      } else {
        // track page view
        trackEvent();
      }
    }
  }
}, 1000);

export default function AnalyticsUmamiProvider() {
  const { data: session } = useSession();
  const trackingId = useTrackingId();

  useActivityTracker();

  useEffect(() => {
    // Track app-load event once per SPA load (hard refresh retriggers this)
    if (trackingId && !isAppLoadTracked) {
      isAppLoadTracked = true;
      trackEvent(AnalyticsEvents.APP_LOAD);
    }
  }, [trackingId]);

  useEffect(() => {
    // Identify user with tracking ID immediately on first visit
    if (trackingId) {
      identifyUser(
        trackingId,
        session?.user?.email || undefined,
        session?.user?.name || undefined,
        session?.user?.id,
      );
    }
  }, [trackingId, session]);

  useEffect(() => {
    window.umamiBeforeSendHandler = (
      type: string,
      payload: UmamiEvent["payload"],
    ) => {
      const url = payload.url as string;
      if (url) {
        // Exclude admin paths from tracking
        // url is absolute, e.g. https://example.com/admin/...
        // we only want to check the pathname
        const pathname = new URL(url).pathname;
        if (pathname.startsWith("/admin")) {
          return false;
        }
      }

      if (!payload.id) {
        if (trackingId) {
          payload.id = trackingId;
        } else {
          // Queue events until we have a tracking ID
          queuedEvents.push({ type, payload });
          return false;
        }
      }

      return payload;
    };
  }, [trackingId]);

  if (!ANALYTICS_CONFIG.websiteId) {
    return null;
  }

  return (
    <Script
      src={ANALYTICS_CONFIG.scriptSrc}
      data-website-id={ANALYTICS_CONFIG.websiteId}
      data-host-url={ANALYTICS_CONFIG.apiPath}
      data-before-send="umamiBeforeSendHandler"
      strategy="lazyOnload"
    />
  );
}
